import React, { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ACCOUNT_TYPES } from '../../constants/account-constants';
import { DEF_PG_SIZE_SUB_MIN } from '../../constants/maximum-page-size';
import { useLocale } from '../../contexts/localeContext';
import { TableContextProvider } from '../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import TagColumns from '../../models/ColumnDefinitions/CredentialColumns';
import { genericError } from '../../models/GenericError';
import { ListData } from '../../models/ListData';
import { ListQuery } from '../../models/ListQuery';
import { Lookup } from '../../models/Lookup';
import { Person } from '../../models/Person';
import TagSearch from '../../models/search/CredentialSearch';
import { DeleteEntityResult } from '../../models/services/DeleteEntityResult';
import { Tag } from '../../models/Tag';
import { deleteCredential, deleteCredentialWithBioTemplate, getBioTemplateById, getCredentialById, getMasterCredentials, saveCredential } from '../../services/credentialsService';
import { enrolVingCard, generateMobileAccessId } from '../../services/executeCommandService';
import { convertDateToString, getTime } from '../../services/utilities/dateFormatting';
import { getErrorMessage, showLoadItemError } from '../../services/utilities/ServiceError';
import { buildCardData } from '../../utilities/CredentialUtil';
import { appendFilter } from '../../utilities/filters';
import { useDialog } from '../dialog/DialogContext';
import EnrolWizard from '../enroll-credentials/enrol-wizard';
import TableTab from '../tabs/table-tab';

type Props = {
    itemId: string,
    setBusy: (boolean) => void,
    onCredentialTabUpdate: () => void;
    currentItem: Person;
}

const CREDENTIAL_TYPE = {
    CARD: 0,
    QR_CODE: 1
} as const;

const EnrolCredentials: FC<Props> = ({ itemId, setBusy, onCredentialTabUpdate, currentItem }) => {
    const { translate } = useLocale();
    const [lookup] = useState<Lookup[]>([]);
    const [showEnrollCredentialWizard, setShowEnrollCredentialWizard] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const initialCredential: Tag = {
        id: 0,
        code: '',
        tagDisplay: '',
        type: '',
        typeId: "0",
        codeUntruncated: '',
        startDate: undefined,
        startTime: '',
        expiryDate: undefined,
        expiryTime: '',
        statusId: '1',
        isCodeModified: false
    };
    const [currentCredential, setCurrentCredential] = useState(initialCredential)
    const dialog = useDialog();

    useEffect(() => {
        dataRefresh((query) => getCredentialsWithEnrolmentId(query));
    }, [itemId, showEnrollCredentialWizard])

    const getCredentialsWithEnrolmentId = (listParameters) => {
        return getMasterCredentials(translate, { ...listParameters, filters: appendFilter(itemId, 'Master', 'tag.master.id', listParameters?.filters) });
    }

    const buildCardInfo = (card: any) => {
        if (card.vingCardType !== undefined) {
            encodeVingcard(card);
            return;
        }

        const saveState = buildCardData({
            ...card,
            id: currentCredential.id,
            masterId: +itemId,
            numberOfBits: card.numberOfBits ? card.numberOfBits : currentCredential.numberOfBits,
            isCodeModified: card.code !== currentCredential.code, // Set the flag if the code is modified
        }, translate);
        saveCredentials(saveState);
    }

    const encodeVingcard = (card) => {
        setBusy(true)

        const vingcard = {
            doors: card.selectedDoors,
            masterId: itemId,
            userGroup: card.vingCardType,
            expireTime: card.userExpirationDate && card.userExpirationTime ? `${convertDateToString(card.userExpirationDate).replaceAll("/", "")}T${getTime(card.userExpirationTime)}` : "",
            timeSchedule: card.timeSchedule,
            checkoutTime: card.checkOutDate && card.checkOutTime ? `${convertDateToString(card.checkOutDate).replaceAll("/", "")}T${getTime(card.checkOutTime)}` : "",
            checkinTime: card.checkInDate && card.checkInTime ? `${convertDateToString(card.checkInDate).replaceAll("/", "")}T${getTime(card.checkInTime)}` : "",
            roomNumber: card.roomNumber
        }

        enrolVingCard(vingcard, translate).then((data) => {
            toast.success(translate.byKey(TranslationKeys.cardEncodedSuccessfully))
        }).catch((error) => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        }).finally(() => {
            setBusy(false)
        })
    }

    const requestMobileCredential = (values) => {
        setBusy(true);
        generateMobileAccessId(parseInt(values.accountType), currentItem.id?.toString() ?? '', values.userEmail, values.usedWith)
            .then((response) => {
                values.codeUntruncated = response.data.tagCode;
                values.code = response.data.tagCode;
                values.maIdentifier = response.data.mobileAccessIdentifier;
                values.masterId = currentItem.id;

                // HID Mobile Access - still need to save the credential.
                if (values.accountType === ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS.toString()) {
                    saveCredentials(values);
                }
                // STid mobile - credential is already saved.
                else {
                    closeEnrolWizard();
                    toast.success(translate.byKey(TranslationKeys.enrolmentSaved));
                }

            }).catch((err) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(err) })
            }).finally(() => {
                setBusy(false)
            })
    }

    const closeEnrolWizard = () => {
        setShowEnrollCredentialWizard(false);
        setCurrentCredential(initialCredential)
    }

    const saveCredentials = (saveState) => {
        const isCreateNew = saveState.id === 0 ? true : false;
        saveCredential(saveState, isCreateNew)
            .then(success => {
                toast.success(translate.byKey(TranslationKeys.enrolmentSaved));
                if (isCreateNew) {
                    onCredentialTabUpdate()
                }
                closeEnrolWizard();
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
            })
            .finally(() => {
                setBusy(false);
            })
    }

    const onDelete = async (id: string, isCustomDeleteKey?: boolean): Promise<DeleteEntityResult> => {
        setBusy(true);
        if (isCustomDeleteKey) {
            return new Promise((resolve, reject) => {
                deleteCredentialWithBioTemplate(id)
                    .then(deleteResult => {
                        resolve(deleteResult);
                        onCredentialTabUpdate()
                        setBusy(false);
                    },
                        deleteResult => {
                            reject(deleteResult);
                            setBusy(false);
                        })
            });
        } else {
            return new Promise((resolve, reject) => {
                deleteCredential(id)
                    .then(deleteResult => {
                        resolve(deleteResult);
                        onCredentialTabUpdate()
                        setBusy(false);
                    },
                        deleteResult => {
                            reject(deleteResult);
                            setBusy(false);
                        })
            });
        }
    }

    const editUserCredentials = async (id: string) => {
        try {
            const credential = await getCredentialById(id, translate);
            setCurrentCredential(credential);
            setShowEnrollCredentialWizard(true);
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
        }
    }

    function base64ToBlob(base64String: string): Promise<Blob | null> {
        const image = new Image();
        const canvas = document.createElement('canvas');
        const padding = 20; // Adjust the padding size as needed.

        return new Promise((resolve, reject) => {
            image.onload = () => {
                const context = canvas.getContext('2d');
                if (!context) {
                    dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(genericError) })
                    return;
                }

                const aspectRatio = image.width / image.height;
                const width = 300; // Adjust the desired width of the canvas.
                const height = width / aspectRatio;
                canvas.width = width + 2 * padding;
                canvas.height = height + 2 * padding;
                context.fillStyle = 'white'; // Background color
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.drawImage(image, padding, padding, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Error converting canvas to blob.'));
                        }
                    },
                    'image/png', // Adjust the image type accordingly.
                    1 // 1 means full quality, adjust as needed.
                );
            };
            image.onerror = (error) => reject(error);
            image.src = `data:image/png;base64,${base64String}`;
        });
    }

    const downloadQRCode = async (item: any) => {

        try {
            const credential = await getBioTemplateById(item.bioTemplateId);
            const blob = await base64ToBlob(credential.qrCode);

            if (!blob) {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(genericError) })
                return;
            }

            const a = document.createElement('a');
            const url = URL.createObjectURL(blob);
            a.href = url;
            a.download = 'QR_Code.png'; // Adjust the file name and extension accordingly.
            document.body.appendChild(a);
            a.click(); // Cleanup
            setTimeout(() => {
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { setShowEnrollCredentialWizard(false); });
        }
    }
    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <>
        <TableContextProvider
            dataMethod={(listParameters) => getCredentialsWithEnrolmentId(listParameters)}
            tableDescriptor={TagColumns}
            searchDescriptor={TagSearch}
            useTable
            usePagination
            useFilter
            useSearch
            useSort
            manualDataRefresh={manualDataRefresh}
            deleteMethod={onDelete}
            editMethod={editUserCredentials}
            downloadMethod={downloadQRCode}
            maxPageSize={DEF_PG_SIZE_SUB_MIN}
            customDeleteKey={{ idKey: 'bioTemplateId', nameKey: 'code' }}
            lookup={lookup}
            customEdit={{ fieldName: "typeId", comparisonValue: CREDENTIAL_TYPE.CARD }}
            customDownload={{ fieldName: "typeId", comparisonValue: CREDENTIAL_TYPE.QR_CODE }}>
            <TableTab
                title={translate.byKey(TranslationKeys.assignedCredentials)}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                setShowModal={setShowEnrollCredentialWizard}
                selectRows={false}
                cyName='enrolCredentials'>
                <button
                    type="button"
                    className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                    id="enrolButton"
                    onClick={() => { setShowEnrollCredentialWizard(true) }}>{translate.byKey(TranslationKeys.enrol)}
                </button>
            </TableTab>
        </TableContextProvider>
        <EnrolWizard
            onClose={() => closeEnrolWizard()}
            isOpen={showEnrollCredentialWizard}
            onCardSave={buildCardInfo}
            currentCredential={currentCredential}
            currentItem={currentItem}
            onRequestMobileCredential={requestMobileCredential}
            updateBadge={onCredentialTabUpdate} />
    </>
}

export default EnrolCredentials;
